import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface MasterDrill {
  id: string;
  drillCode: string;
  drillName: string;
  drillType?: string;
  courtPosition?: string;
  shuttlecockType?: string;
  netHeight?: number;
  description?: string;
  durationMinutes?: number;
  equipmentNeeded?: string[];
  setupInstructions?: string;
  executionSteps?: string[];
  coachingPoints?: string[];
  referenceVideos?: string[];
  areaOfImprovementOptions?: string[];
  suggestedPracticesOptions?: string[];
  difficultyLevel?: number;
  isActive: boolean;
  showOnHomepage?: boolean;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface AdminMasterDrillsProps {
  sport?: string;
}

const AdminMasterDrills: React.FC<AdminMasterDrillsProps> = ({ sport = 'Badminton' }) => {
  const [drills, setDrills] = useState<MasterDrill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDrill, setSelectedDrill] = useState<MasterDrill | null>(null);
  const [editingDrill, setEditingDrill] = useState<MasterDrill | null>(null);
  const selectedSport = sport || 'Badminton';

  const [formData, setFormData] = useState({
    drillCode: '',
    drillName: '',
    drillType: '',
    courtPosition: '',
    shuttlecockType: '',
    netHeight: '',
    description: '',
    durationMinutes: '',
    equipmentNeeded: [] as string[],
    setupInstructions: '',
    executionSteps: [] as string[],
    coachingPoints: [] as string[],
    referenceVideos: [] as string[],
    areaOfImprovementOptionsText: '',
    suggestedPracticesOptionsText: '',
    difficultyLevel: 1,
    isActive: true,
    showOnHomepage: false,
    displayOrder: 0,
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  const loadDrills = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-drills?sport=${selectedSport}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setDrills(data.data || []);
      } else {
        setError(data.message || 'Failed to load drills');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load drills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrills();
  }, [selectedSport]);

  const handleAddNew = () => {
    setEditingDrill(null);
    setFormData({
      drillCode: '',
      drillName: '',
      drillType: '',
      courtPosition: '',
      shuttlecockType: '',
      netHeight: '',
      description: '',
      durationMinutes: '',
      equipmentNeeded: [],
      setupInstructions: '',
      executionSteps: [],
      coachingPoints: [],
      referenceVideos: [],
      areaOfImprovementOptionsText: '',
      suggestedPracticesOptionsText: '',
      difficultyLevel: 1,
      isActive: true,
      showOnHomepage: false,
      displayOrder: 0,
    });
    setDialogOpen(true);
  };

  const handleEdit = (drill: MasterDrill) => {
    setEditingDrill(drill);
    setFormData({
      drillCode: drill.drillCode || '',
      drillName: drill.drillName || '',
      drillType: drill.drillType || '',
      courtPosition: drill.courtPosition || '',
      shuttlecockType: drill.shuttlecockType || '',
      netHeight: drill.netHeight?.toString() || '',
      description: drill.description || '',
      durationMinutes: drill.durationMinutes?.toString() || '',
      equipmentNeeded: drill.equipmentNeeded || [],
      setupInstructions: drill.setupInstructions || '',
      executionSteps: drill.executionSteps || [],
      coachingPoints: drill.coachingPoints || [],
      referenceVideos: drill.referenceVideos || [],
      areaOfImprovementOptionsText: Array.isArray(drill.areaOfImprovementOptions) ? drill.areaOfImprovementOptions.join(', ') : '',
      suggestedPracticesOptionsText: Array.isArray(drill.suggestedPracticesOptions) ? drill.suggestedPracticesOptions.join(', ') : '',
      difficultyLevel: drill.difficultyLevel || 1,
      isActive: drill.isActive ?? true,
      showOnHomepage: drill.showOnHomepage === true,
      displayOrder: drill.displayOrder || 0,
    });
    setDialogOpen(true);
  };

  const handleView = (drill: MasterDrill) => {
    setSelectedDrill(drill);
    setViewDialogOpen(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        sport: selectedSport,
        ...formData,
        durationMinutes: formData.durationMinutes ? parseInt(formData.durationMinutes) : null,
        netHeight: formData.netHeight ? parseFloat(formData.netHeight) : null,
        difficultyLevel: formData.difficultyLevel,
        areaOfImprovementOptions: String(formData.areaOfImprovementOptionsText || '')
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0),
        suggestedPracticesOptions: String(formData.suggestedPracticesOptionsText || '')
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0),
        displayOrder: formData.displayOrder,
      };

      const url = editingDrill
        ? `/api/admin/master-drills/${editingDrill.id}`
        : '/api/admin/master-drills';
      const method = editingDrill ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setDialogOpen(false);
        loadDrills();
      } else {
        setError(data.message || 'Failed to save drill');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save drill');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this drill?')) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-drills/${id}?sport=${selectedSport}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const data = await response.json();
      if (data.success) {
        loadDrills();
      } else {
        setError(data.message || 'Failed to delete drill');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete drill');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Master Drills Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Sport: {selectedSport}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDrills}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add Drill
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !drills.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Home Page</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>
                      No drills found. Click "Add Drill" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                drills.map((drill) => (
                  <TableRow key={drill.id}>
                    <TableCell>{drill.drillCode}</TableCell>
                    <TableCell>{drill.drillName}</TableCell>
                    <TableCell>{drill.drillType || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={drill.difficultyLevel || 1}
                        size="small"
                        color={drill.difficultyLevel && drill.difficultyLevel >= 4 ? 'error' : drill.difficultyLevel && drill.difficultyLevel >= 3 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>{drill.durationMinutes ? `${drill.durationMinutes} min` : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={drill.showOnHomepage ? 'Visible' : 'Hidden'}
                        size="small"
                        color={drill.showOnHomepage ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={drill.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={drill.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleView(drill)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(drill)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(drill.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingDrill ? 'Edit Drill' : 'Add New Drill'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Drill Code *"
                value={formData.drillCode}
                onChange={(e) => setFormData({ ...formData, drillCode: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Drill Name *"
                value={formData.drillName}
                onChange={(e) => setFormData({ ...formData, drillName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Drill Type"
                value={formData.drillType}
                onChange={(e) => setFormData({ ...formData, drillType: e.target.value })}
                placeholder="e.g., 4-corners, half-court, full-court"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Court Position"
                value={formData.courtPosition}
                onChange={(e) => setFormData({ ...formData, courtPosition: e.target.value })}
                placeholder="e.g., front, mid, back court"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shuttlecock Type"
                value={formData.shuttlecockType}
                onChange={(e) => setFormData({ ...formData, shuttlecockType: e.target.value })}
                placeholder="e.g., feather, synthetic"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Net Height (meters)"
                type="number"
                value={formData.netHeight}
                onChange={(e) => setFormData({ ...formData, netHeight: e.target.value })}
                inputProps={{ step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.durationMinutes}
                onChange={(e) => setFormData({ ...formData, durationMinutes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficultyLevel}
                  label="Difficulty Level"
                  onChange={(e) => setFormData({ ...formData, difficultyLevel: e.target.value as number })}
                >
                  <MenuItem value={1}>1 - Beginner</MenuItem>
                  <MenuItem value={2}>2 - Easy</MenuItem>
                  <MenuItem value={3}>3 - Intermediate</MenuItem>
                  <MenuItem value={4}>4 - Advanced</MenuItem>
                  <MenuItem value={5}>5 - Expert</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Setup Instructions"
                value={formData.setupInstructions}
                onChange={(e) => setFormData({ ...formData, setupInstructions: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Area of Improvement (comma separated)"
                value={formData.areaOfImprovementOptionsText}
                onChange={(e) => setFormData({ ...formData, areaOfImprovementOptionsText: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Suggested Practices (comma separated)"
                value={formData.suggestedPracticesOptionsText}
                onChange={(e) => setFormData({ ...formData, suggestedPracticesOptionsText: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showOnHomepage === true}
                    onChange={(e) => setFormData({ ...formData, showOnHomepage: e.target.checked })}
                  />
                }
                label="Show on Home Page preview"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading || !formData.drillCode || !formData.drillName}>
            {loading ? <CircularProgress size={20} /> : editingDrill ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Drill Details</DialogTitle>
        <DialogContent>
          {selectedDrill && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedDrill.drillName}</Typography>
                  <Typography variant="body2" color="textSecondary">Code: {selectedDrill.drillCode}</Typography>
                </Grid>
                {selectedDrill.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Description</Typography>
                    <Typography variant="body2">{selectedDrill.description}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Type</Typography>
                  <Typography variant="body2">{selectedDrill.drillType || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Difficulty</Typography>
                  <Chip
                    label={selectedDrill.difficultyLevel || 1}
                    size="small"
                    color={selectedDrill.difficultyLevel && selectedDrill.difficultyLevel >= 4 ? 'error' : selectedDrill.difficultyLevel && selectedDrill.difficultyLevel >= 3 ? 'warning' : 'success'}
                  />
                </Grid>
                {selectedDrill.durationMinutes && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Duration</Typography>
                    <Typography variant="body2">{selectedDrill.durationMinutes} minutes</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    label={selectedDrill.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={selectedDrill.isActive ? 'success' : 'default'}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedDrill && (
            <Button onClick={() => { setViewDialogOpen(false); handleEdit(selectedDrill); }} variant="contained">
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMasterDrills;

